import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Button } from '@neo/primitives/button'
import { Label } from '@neo/primitives/label'
import { Tooltip } from '@neo/primitives/tooltip'
import { Info } from 'lucide-react'
import type { ROriginationURIInternal } from 'v2/types/api/settings'
import { z } from 'zod'
import {
  buildStaticSipUri,
  detectForeignPlatform,
  normalizeSipUserInput,
  normalizeUriInput,
  SIP_PLATFORMS,
  SIP_USER_INVALID_MESSAGE,
  SIP_USER_PATTERN,
  type SipPlatformKey,
  type SipTransport,
  uriMismatchMessage,
} from '../platform-config'

export interface OriginationURIFormValues {
  name: string
  uri: string
  /** Fixed SIP user (zentrunk sip_user) — OpenAI's project id */
  sip_user: string
  authentication_needed: boolean
  username: string
  password: string
}

export interface UriModalPlatformOptions {
  /** Selected platform; undefined/custom keeps the classic free-form URI input */
  platform?: SipPlatformKey
  transport: SipTransport
  onTransportChange: (transport: SipTransport) => void
  region?: string
}

/** Fixed-host platforms: read-only URI + transport pills */
const StaticUriField = ({ platformOpts }: { platformOpts: UriModalPlatformOptions }) => {
  const { platform, transport, onTransportChange, region } = platformOpts
  if (!platform) return null
  const config = SIP_PLATFORMS[platform]
  const uri = buildStaticSipUri(platform, transport, region)

  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-1">
        <Label>URI</Label>
        <Tooltip content={`All agents on ${config.label} share this SIP server address.`}>
          <Info className="h-4 w-4" />
        </Tooltip>
      </span>
      <div
        className="bg-bg3 border rounded-md px-3 py-2 text-p-400 text-foreground font-jetbrains truncate"
        data-testid="static-platform-uri"
      >
        sip:{uri}
      </div>
      <span className="text-s-400 text-secondary">
        You can change the transport added to the SIP URL.
      </span>
      <div className="flex gap-2">
        {(config.transports ?? []).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onTransportChange(option)}
            className={`rounded-md border px-3 py-1 text-s-500 ${
              option === transport
                ? 'border-primary text-foreground ring-1 ring-primary'
                : 'text-secondary hover:bg-bg3'
            }`}
            data-testid={`button-transport-${option}`}
          >
            {option.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  )
}

type UriFieldConfig = GenericFieldConfig<OriginationURIFormValues>[number]

/** Fixed-host platforms: value composed at submit; nothing to validate here */
const staticUriField = (platformOpts: UriModalPlatformOptions): UriFieldConfig => ({
  name: 'uri',
  type: 'custom',
  className: 'col-span-2',
  component: () => <StaticUriField platformOpts={platformOpts} />,
  zodValidation: () => z.any(),
})

/** LiveKit: editable subdomain between fixed sip:/suffix adornments */
const templatedUriField = (
  platform: SipPlatformKey,
  platformConfig: (typeof SIP_PLATFORMS)[SipPlatformKey],
): UriFieldConfig => ({
  name: 'uri',
  type: 'custom',
  className: 'col-span-2',
  component: ({ form }) => {
    const subdomain = (form.getFieldValue('uri') as string) || ''
    return (
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-1">
          <Label htmlFor="platform-subdomain-input">URI</Label>
          <Tooltip
            content={`Type just the subdomain, or paste your full ${platformConfig.label} URL and we'll trim it for you.`}
          >
            <Info className="h-4 w-4" />
          </Tooltip>
        </span>
        <div className="flex items-stretch overflow-hidden rounded-md border font-jetbrains tracking-normal focus-within:ring-1 focus-within:ring-primary">
          <span className="flex items-center bg-bg3 px-2 text-p-400 text-secondary whitespace-nowrap">
            sip:
          </span>
          <input
            id="platform-subdomain-input"
            aria-label={`${platformConfig.label} subdomain`}
            value={subdomain}
            inputMode="url"
            placeholder={platformConfig.uriPlaceholder}
            onChange={(e) => {
              const { value } = normalizeUriInput(e.target.value, platform)
              form.setFieldValue('uri', value)
              form.validateField('uri', 'change')
            }}
            className="flex-1 min-w-0 bg-transparent px-2 py-2 text-p-400 text-foreground outline-none"
            data-testid="input-platform-subdomain"
          />
          <span className="flex items-center bg-bg3 px-2 text-p-400 text-secondary whitespace-nowrap">
            {platformConfig.uriSuffix}
          </span>
        </div>
      </div>
    )
  },
  zodValidation: () =>
    z
      .string()
      .min(1, 'Subdomain is required')
      .refine(
        (val) => !detectForeignPlatform(val, platform),
        (val) => ({
          message: uriMismatchMessage(
            detectForeignPlatform(val, platform)?.label ?? 'different platform',
            platformConfig.label,
          ),
        }),
      )
      .refine((val) => /^[a-zA-Z0-9._-]+$/.test(val), {
        message: 'Subdomain can contain only letters, numbers, dots, hyphens and underscores',
      }),
})

/** Custom platform / no platform: the classic free-form FQDN/IP input */
const freeformUriField = (): UriFieldConfig => ({
  name: 'uri',
  componentType: 'input',
  inputProps: {
    inputMode: 'url',
    placeholder: 'Enter FQDN or IP address',
    label: (
      <span className="flex items-center gap-1">
        URI{' '}
        <Tooltip
          content="URI FQDN or IP address of your VoIP infrastructure.
Exclude prefixes (https:// or http:// or www or sip).
To use TLS as transport use example.com:5061;transport=tls
and for TCP example.com;transport=tcp"
        >
          <Info className="h-4 w-4" />
        </Tooltip>
      </span>
    ),
  },
  className: 'col-span-2',
  eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
  zodValidation: z
    .string()
    .min(1, 'URI is required')
    .refine((val) => !val.includes('@'), { message: "URI cannot contain '@'" }),
})

/**
 * sipUser platforms (OpenAI): the fixed host stays read-only like static mode,
 * but the user part is editable and stored as zentrunk's sip_user — visually
 * one URI (sip: [project id] @host), same shape as the LiveKit subdomain input.
 * The URI string itself is still composed host-only at submit; the project id
 * travels as a separate payload field, never inside the URI (trunk-central
 * rejects '@' in the uri).
 */
const sipUserUriField = (
  platform: SipPlatformKey,
  platformConfig: (typeof SIP_PLATFORMS)[SipPlatformKey],
  platformOpts: UriModalPlatformOptions,
): UriFieldConfig => ({
  name: 'sip_user',
  type: 'custom',
  className: 'col-span-2',
  component: ({ form }) => {
    const sipUser = (form.getFieldValue('sip_user') as string) || ''
    const { transport, onTransportChange, region } = platformOpts
    const host = buildStaticSipUri(platform, transport, region).split(';')[0]
    const sipUserConfig = platformConfig.sipUser
    return (
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-1">
          <Label htmlFor="platform-sip-user-input">{sipUserConfig?.label ?? 'SIP User'}</Label>
          {sipUserConfig?.helper && (
            <Tooltip content={sipUserConfig.helper}>
              <Info className="h-4 w-4" />
            </Tooltip>
          )}
        </span>
        <div className="flex items-stretch border rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-primary">
          <span className="flex items-center bg-bg3 px-2 text-p-400 text-secondary whitespace-nowrap">
            sip:
          </span>
          <input
            id="platform-sip-user-input"
            aria-label={`${platformConfig.label} ${sipUserConfig?.label ?? 'SIP user'}`}
            value={sipUser}
            placeholder={sipUserConfig?.placeholder}
            onChange={(e) => {
              // paste-forgiveness: a full sip:proj_x@host;transport=tls pastes
              // down to just the user part
              form.setFieldValue('sip_user', normalizeSipUserInput(e.target.value))
              form.validateField('sip_user', 'change')
            }}
            className="flex-1 min-w-0 bg-transparent px-2 py-2 text-p-400 text-foreground outline-none"
            data-testid="input-platform-sip-user"
          />
          <span className="flex items-center bg-bg3 px-2 text-p-400 text-secondary whitespace-nowrap">
            @{host}
          </span>
        </div>
        <div className="flex gap-2">
          {(platformConfig.transports ?? []).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onTransportChange(option)}
              className={`rounded-md border px-3 py-1 text-s-500 ${
                option === transport
                  ? 'border-primary text-foreground ring-1 ring-primary'
                  : 'text-secondary hover:bg-bg3'
              }`}
              data-testid={`button-transport-${option}`}
            >
              {option.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    )
  },
  // Charset enforced HERE because the zentrunk public API silently ignores
  // invalid sip_user values (2xx, value never stored) — without this the
  // project id would "save" and OpenAI would reject every call.
  zodValidation: () =>
    z
      .string()
      .min(1, `${platformConfig.sipUser?.label ?? 'SIP user'} is required`)
      .refine((val) => SIP_USER_PATTERN.test(val), { message: SIP_USER_INVALID_MESSAGE }),
})

const buildUriField = (platformOpts?: UriModalPlatformOptions): UriFieldConfig => {
  const platform = platformOpts?.platform
  const platformConfig = platform ? SIP_PLATFORMS[platform] : undefined
  if (platformOpts && platform && platformConfig?.sipUser) {
    return sipUserUriField(platform, platformConfig, platformOpts)
  }
  if (platformOpts && platformConfig?.uriMode === 'static') {
    return staticUriField(platformOpts)
  }
  if (platform && platformConfig?.uriMode === 'templated') {
    return templatedUriField(platform, platformConfig)
  }
  return freeformUriField()
}

export const originationURIFormFields = (
  editData?: ROriginationURIInternal | null,
  isPasswordEnabled: boolean = false,
  onPasswordChangeClick?: () => void,
  platformOpts?: UriModalPlatformOptions,
): GenericFieldConfig<OriginationURIFormValues> => {
  const isEditing = !!editData
  const uriField = buildUriField(platformOpts)

  return [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      inputProps: {
        placeholder: 'Enter origination URI name',
      },
      className: 'col-span-2',
      eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
      zodValidation: z
        .string()
        .min(1, 'Name is required')
        .max(100, 'Name must be less than 100 characters'),
    },
    uriField,
    {
      name: 'authentication_needed',
      componentType: 'switch',
      label: 'Authentication needed',
      className: 'col-span-2',
      zodValidation: z.boolean().default(false),
    },
    {
      name: 'username',
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter username',
        label: (
          <span className="flex items-center gap-1">
            Username{' '}
            <Tooltip
              content="Provide a username that will be 
sent in response to an auth challenge 
of the SIP INVITE."
            >
              <Info className="h-4 w-4" />
            </Tooltip>
          </span>
        ),
      },
      className: 'col-span-2',
      onChangeListenTo: ['authentication_needed'],
      visible: (values) => values.authentication_needed,
      eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
      zodValidation: (values) =>
        values.authentication_needed
          ? z.string().min(1, 'Username is required')
          : z.string().optional(),
    },
    {
      name: 'password',
      componentType: 'inputPassword',
      label: isEditing ? (
        <Label className="flex items-center justify-between">
          <span>Password</span>
          {!isPasswordEnabled && (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                onPasswordChangeClick?.()
              }}
              variant="link"
              size="sm"
              className="h-auto p-0"
              data-testid="button-change-uri-password"
            >
              Change Password
            </Button>
          )}
        </Label>
      ) : (
        // No info icon: the inline validation messages already state the rules
        'Password'
      ),
      inputProps: {
        placeholder: isEditing
          ? isPasswordEnabled
            ? 'Enter new password'
            : '••••••••'
          : 'Enter password',
        disabled: isEditing && !isPasswordEnabled,
      },
      // Copy button beside the built-in eye toggle, same treatment as the
      // outbound credential modal: this password is needed when configuring the
      // SIP platform and the API never returns it after saving.
      getDynamicProps: (values) => ({
        inputProps: {
          endIcon:
            values.password && (!isEditing || isPasswordEnabled) ? (
              <ClipboardCopyButton
                textToCopy={values.password}
                tooltipText="Copy password"
                size="icon"
                variant="ghost"
                iconSize={14}
                className="h-4 w-4"
                data-testid="button-copy-uri-password"
              />
            ) : undefined,
        },
      }),
      className: 'col-span-2',
      onChangeListenTo: ['authentication_needed'],
      visible: (values) => values.authentication_needed,
      eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
      zodValidation: (values) => {
        if (!values.authentication_needed) return z.string().optional()
        const passwordValue = values.password
        if (isEditing && (!passwordValue || passwordValue === '**********'))
          return z.string().optional()
        return z.string().superRefine((val, ctx) => {
          if (!val || val.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Password is required',
            })
            return
          }

          if (val.length < 5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Password must be at least 5 characters',
            })
            return
          }

          if (val.length > 20) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Password must be less than 20 characters',
            })
            return
          }

          if (!/^[a-zA-Z0-9~!@#$%^&*()_+]+$/.test(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'Password must contain only alphanumeric characters and special characters (~!@#$%^&*()_+)',
            })
            return
          }

          if (!/[~!@#$%^&*()_+]/.test(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Password must contain at least one special character (~!@#$%^&*()_+)',
            })
            return
          }
        })
      },
    },
    {
      // Same view-once notice as the outbound credential modal (Tejal, 2026-08-03)
      name: 'uri_password_usage_note',
      type: 'basic',
      component: ({ form }) => {
        const authNeeded = form.useStore((state) => state.values.authentication_needed)
        if (!authNeeded) return null
        if (isEditing && !isPasswordEnabled) return null
        return (
          <div className="col-span-2" data-testid="alert-uri-password-usage-note">
            <Alert
              variant="default"
              content="This password is only shown once. Copy and save it now. You'll need it to configure your SIP platform."
            />
          </div>
        )
      },
    },
  ]
}
